import { CommonConstants } from '@/config/constant';
import DOMPurify from 'dompurify';
import router from '@/router';

const formatDownLine = function(text) {
  if (text) {
    return text.split(/\n/g);
  }
};

const formatLongText = function(text) {
  if (text.length > 10) {
    return text.replace(CommonConstants.SPACE_REGEX, '').match(/.{1,10}/g);
  } else {
    return text.split(/\n/g);
  }
};

const stripTags = (string) => {
  return string.replace(CommonConstants.STRIP_TAGS, '');
};

const formatChargeType = (type) => {
  return type == CommonConstants.IS_CONVENIENCE_STORE ? 'コンビニ払い' : type;
};

const formatLocalString = (val) => {
  if (!val || !Number(val)) {
    return 0;
  }
  return Number(val).toLocaleString();
};

const formatStringMoneyToNumber = (item) => {
  if (!item) {
    return '';
  }
  return Number(item.replace(/,/g, ''));
};

const formatStringCurrency = (item) => {
  if (!item) {
    return '';
  }
  const moneyStr = Number(item.toString().replace(CommonConstants.COMMA_REGEX, ''));
  return moneyStr.toString().replace(CommonConstants.STRING_REGEX, '').replace(CommonConstants.COMMA_NUMBER_REGEX, ',');
};

const convertNumberToArray = (number) => {
  if (!number || number <= 0) {
    return [];
  }

  return Array.from({ length: number }, (_, index) => index + 1);
};

const generateRandomId = () => {
  return Math.random().toString(36).substring(2);
};

const convertNumberToArrayWithKey = (number) => {
  return Array.from({ length: number + 1 }, (_, index) => ({
    key: generateRandomId(),
    value: index,
  }));
};

const formatEndLineAndLinkTag = (input) => {
  if (!input) return '';
  const urls = input.match(CommonConstants.URL_VALIDATION);
  if (!urls) return input;
  const stringWithLinks = input.replace(
    CommonConstants.URL_VALIDATION,
    (url) => `<a class="link-main" href="${url}" target="_blank">${url}</a>`
  );
  return DOMPurify.sanitize(stringWithLinks);
};

const mergeArrays = (arrayA, arrayB) => {
  const mapB = new Map(arrayB.map(item => [item.id, item]));
  return (arrayA || []).map(itemA => ({
    ...itemA,
    times: Number((mapB.get(itemA.id) || {}).times) || 0
  }));
};

const formatHashUrl = (item) => {
  if (!item) {
    return '';
  }
  return item.replace(CommonConstants.HASH_REGEX, '');
};

const formatHideDigits = (number, hideNumber = '****') => {
  const visiblePart = String(number).slice(hideNumber.length);
  return hideNumber + visiblePart;
};

const formatUrlWithoutQuery = (url) => {
  const newQuery = { ...url.query };
  delete newQuery.error;
  router.replace({
    ...url,
    query: newQuery,
  });
};

const generateRandomNumber = (length) => {
  return Array.from({ length }, () => CommonConstants.NUMBER[Math.floor(Math.random() * CommonConstants.NUMBER.length)]).join('');
};

const formatBirthday = (string) => {
  let matches = string.match(CommonConstants.BIRTHDAY_VALIDATION_REGEX);
  if (!matches) {
    return null;
  }
  let selectedYear = parseInt(matches[1]);
  let selectedMonth = parseInt(matches[2]);
  let selectedDay = parseInt(matches[3]);
  return { selectedDay: selectedDay, selectedMonth: selectedMonth, selectedYear: selectedYear };
};

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const findMinDate = (date1, date2) => {
  const parseDate = (date) => {
    if (!date) return null;
    if (CommonConstants.DATE_VALIDATION_REGEX.test(date)) {
      return new Date(`${date}T00:00:00.000Z`);
    }
    return new Date(date);
  };

  const day1 = parseDate(date1);
  const day2 = parseDate(date2);

  if (!day1 && !day2) return null;
  if (!day1) return date2;
  if (!day2) return date1;

  return day1 < day2 ? date1 : date2;
};

const generateCodeVerifier = (length = 43) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let codeVerifier = '';
  for (let i = 0; i < length; i++) {
    codeVerifier += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return codeVerifier;
};

const generateCodeChallenge = async (codeVerifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  const base64String = btoa(String.fromCharCode(...new Uint8Array(hashBuffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return base64String;
};

const decodeBody = (str) => {
  const isBase64 = CommonConstants.BASE64_REGEX.test(str) && str?.length % 4 === 0;
  if (isBase64) {
    try {
      const binary = atob(str);
      const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
      return new TextDecoder().decode(bytes);
    } catch (e) {
      console.warn('Failed to decode base64. Fallback to raw string.', e);
      return str;
    }
  }
  return str;
};

export {
  formatDownLine,
  formatLongText,
  stripTags,
  formatChargeType,
  formatLocalString,
  formatStringMoneyToNumber,
  formatStringCurrency,
  formatHideDigits,
  generateRandomId,
  formatHashUrl,
  convertNumberToArray,
  convertNumberToArrayWithKey,
  formatEndLineAndLinkTag,
  mergeArrays,
  formatUrlWithoutQuery,
  generateRandomNumber,
  formatBirthday,
  capitalizeFirstLetter,
  findMinDate,
  generateCodeVerifier,
  generateCodeChallenge,
  decodeBody,
};
